import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Network, Building2, ShieldCheck, LifeBuoy, Landmark, Receipt, FolderLock, CalendarDays, Sparkles,
  CreditCard, Menu, Moon, Sun, LogOut, Lock, Settings as SettingsIcon, Eye, BrainCircuit, Briefcase, Gavel, Hourglass, Rocket, Scale, FlaskConical,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth, hasFeature } from "@/context/AuthContext";
import { Logo } from "@/components/app/Logo";
import { WorkspaceSwitcher } from "@/components/app/WorkspaceSwitcher";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/chief-of-staff", label: "Chief of Staff", icon: BrainCircuit, feature: "cos" },
  { to: "/app/decisions", label: "Decision Intelligence", icon: Gavel, feature: "cos" },
  { to: "/app/waiting", label: "Waiting On", icon: Hourglass, feature: "cos" },
  { to: "/app/growth", label: "Growth & Allocation", icon: Rocket, feature: "cos" },
  { to: "/app/portfolio", label: "Portfolio", icon: Network, feature: "portfolio" },
  { to: "/app/deals", label: "Deals", icon: Briefcase, feature: "cos" },
  { to: "/app/entities", label: "Entities", icon: Building2 },
  { to: "/app/compliance", label: "Compliance", icon: ShieldCheck },
  { to: "/app/regulatory", label: "Regulatory Watch", icon: Scale, feature: "cos", ownerOnly: true },
  { to: "/app/research-lab", label: "Research validation", icon: FlaskConical, feature: "cos", ownerOnly: true },
  { to: "/app/revival", label: "Revival", icon: LifeBuoy, feature: "revival" },
  { to: "/app/tax", label: "Tax Tracker", icon: Landmark, feature: "tax" },
  { to: "/app/receipts", label: "Receipts", icon: Receipt },
  { to: "/app/vault", label: "Records Vault", icon: FolderLock, feature: "vault" },
  { to: "/app/calendar", label: "Filing Calendar", icon: CalendarDays },
  { to: "/app/assistant", label: "AI Assistant", icon: Sparkles, feature: "ai" },
  { to: "/app/settings", label: "Team & Reminders", icon: SettingsIcon },
  { to: "/app/billing", label: "Billing", icon: CreditCard, ownerOnly: true },
];

export const useTheme = () => {
  const [dark, setDark] = useState(() => localStorage.getItem("aq_theme") !== "light");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("aq_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
};

const NavItems = ({ user, onNav }) => (
  <nav className="space-y-0.5">
    {NAV.filter((n) => !n.ownerOnly || user?.workspace?.role === "owner").map((n) => {
      const locked = n.feature && !hasFeature(user, n.feature);
      return (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNav} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary/10 text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-accent"}`}>
          {({ isActive }) => (
            <>
              <n.icon className={`h-4 w-4 ${isActive ? "text-primary" : ""}`} />
              <span className="flex-1">{n.label}</span>
              {locked && <Lock className="h-3 w-3 opacity-60" />}
            </>
          )}
        </NavLink>
      );
    })}
  </nav>
);

const SidebarBody = ({ user, onNav, logout }) => (
  <div className="flex h-full flex-col">
    <div className="px-5 h-16 flex items-center border-b border-border"><Logo /></div>
    <WorkspaceSwitcher />
    <div className="flex-1 overflow-y-auto p-3"><NavItems user={user} onNav={onNav} /></div>
    <div className="p-4 border-t border-border">
      <div className="text-xs text-muted-foreground">{user?.workspace?.role === "owner" ? "Plan" : "Workspace access"}</div>
      <div className="text-sm font-semibold" data-testid="sidebar-plan-label">{user?.workspace?.role === "owner" ? user?.plan_label : `${user?.workspace?.role === "viewer" ? "View-only" : "Editor"} · ${user?.workspace?.owner_name}`}</div>
      <div className="mt-3 flex items-center gap-2">
        {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> :
          <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{(user?.name || "U")[0]}</div>}
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <button onClick={logout} className="p-2 rounded-md hover:bg-accent" data-testid="logout-btn" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
      </div>
    </div>
  </div>
);

export default function AppLayout() {
  const { user, logout, refresh } = useAuth();
  const [dark, setDark] = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  const doLogout = async () => {
    await logout();
    navigate("/");
  };
  const trialDays = user?.trial_ends_at && !user?.subscribed_plan && user?.role !== "admin" && user?.workspace?.role === "owner"
    ? Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000) : null;
  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 border-r border-border bg-card/60 backdrop-blur-xl z-30">
        <SidebarBody user={user} logout={doLogout} />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72"><SidebarBody user={user} onNav={() => setOpen(false)} logout={doLogout} /></SheetContent>
      </Sheet>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 border-b border-border bg-background/80 backdrop-blur-xl flex items-center gap-3 px-4 sm:px-8">
          <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu"><Menu className="h-5 w-5" /></button>
          <div className="lg:hidden"><Logo /></div>
          <div className="flex-1" />
          {user?.workspace?.role !== "owner" && (
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full border border-blue-500/30 text-blue-500 px-3 py-1 truncate max-w-[60vw]" data-testid="member-banner">
              {user?.workspace?.role === "viewer" && <Eye className="h-3.5 w-3.5" />}
              {user?.workspace?.owner_name}'s workspace · {user?.workspace?.role === "viewer" ? "view-only" : "editor"}
              {Array.isArray(user?.workspace?.entity_ids) && ` · ${user.workspace.entity_ids.length} entit${user.workspace.entity_ids.length === 1 ? "y" : "ies"}`}
            </span>
          )}
          {trialDays != null && (
            <button onClick={() => navigate("/app/billing")} data-testid="trial-banner"
              className={`hidden sm:inline-flex text-xs rounded-full border px-3 py-1 ${trialDays > 0 ? "border-emerald-500/30 text-emerald-600 dark:text-emerald-400" : "border-red-500/40 text-red-500"}`}>
              {trialDays > 0 ? `Professional trial · ${trialDays} days left` : "Trial ended — choose a plan"}
            </button>
          )}
          <Button variant="ghost" size="icon" onClick={() => { setDark(!dark); }} data-testid="theme-toggle" aria-label="Toggle theme">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-[1400px] mx-auto">
          <Outlet context={{ refreshUser: refresh }} />
        </main>
      </div>
    </div>
  );
}
